<!-- this manages the video player functionality like play, pause and picture-in-picture -->
<script setup lang="ts">
	import { ref, computed, onMounted, watch } from "vue";
	import { Capacitor } from "@capacitor/core";
	import { Icon } from "@ptx-autonomous/libs/ui";
	import { DialogLayout } from "@ptx-autonomous/libs/ui";
	import type { VideoUrl } from "@shared/composables/help-files";
	import { useI18n } from "vue-i18n";

	export interface PlaylistItem {
		key: string;
		title: string;
		description?: string;
		videoUrl: VideoUrl;
		playlistName?: string;
		playlistPosition?: number;
	}

	const { t } = useI18n();

	const props = defineProps<{
		videoUrl?: Record<string, string> | string;
		videoTitle?: string;
		videoDescription?: string;
		playlistItems?: PlaylistItem[];
		currentKey?: string;
		/** Render inline filling the parent instead of inside a dialog. */
		fullPage?: boolean;
	}>();
	const isMobileDevice = ref(false);
	const emit = defineEmits<{
		close: [];
		playlistItemChange: [item: PlaylistItem];
	}>();

	const videoElement = ref<HTMLVideoElement>();
	const isLoading = ref(true);
	const hasError = ref(false);
	const retryAttempted = ref(false); // for ipad only
	const skipSeconds = 10;
	const showSkipButtons = ref(false);
	const isIpad = ref(
		/iPad|Macintosh/.test(navigator.userAgent) && "ontouchend" in document,
	);
	const isNativeApp = ref(false);
	const selectedQuality = ref<string>("");
	const isQualityMenuOpen = ref<boolean>(false);
	const activePlaylistItem = ref<PlaylistItem | null>(null);

	// Determine if we're using playlist mode
	const isPlaylistMode = computed(() => {
		return (
			props.playlistItems &&
			props.playlistItems.length > 1 &&
			props.currentKey
		);
	});

	// Find the active playlist item based on currentKey
	watch(
		() => [props.playlistItems, props.currentKey],
		() => {
			if (
				isPlaylistMode.value &&
				props.playlistItems &&
				props.currentKey
			) {
				const item = props.playlistItems.find(
					item => item.key === props.currentKey,
				);
				activePlaylistItem.value =
					item || props.playlistItems[0] || null;
			} else {
				activePlaylistItem.value = null;
			}
			retryAttempted.value = false; // Reset for new video load
		},
		{ immediate: true },
	);

	const effectiveVideoUrl = computed<VideoUrl | undefined>(() => {
		return activePlaylistItem.value
			? activePlaylistItem.value.videoUrl
			: props.videoUrl;
	});

	const effectiveVideoTitle = computed(() => {
		return activePlaylistItem.value
			? activePlaylistItem.value.title
			: props.videoTitle;
	});

	const qualityOptions = computed<string[]>(() => {
		if (
			!effectiveVideoUrl.value ||
			typeof effectiveVideoUrl.value === "string"
		)
			return [];
		const videoUrlObj = effectiveVideoUrl.value as Record<string, string>;
		return Object.keys(videoUrlObj).filter(
			k => k !== "720" && videoUrlObj[k],
		);
	});

	const getPreferredQuality = (options: string[]): string => {
		if (options.length === 0) return "";
		const preferred720 = options.find(
			opt => opt.toLowerCase().includes("720") || opt === "720p",
		);
		return preferred720 || options[0];
	};

	const currentVideoUrl = computed<string | undefined>(() => {
		if (!effectiveVideoUrl.value) return undefined;
		if (typeof effectiveVideoUrl.value === "string")
			return effectiveVideoUrl.value;
		const urlObj = effectiveVideoUrl.value as Record<string, string>;
		// If no quality selected but we have options, use preferred
		if (!selectedQuality.value && qualityOptions.value.length > 0) {
			return urlObj[getPreferredQuality(qualityOptions.value)];
		}
		return urlObj[selectedQuality.value];
	});

	function handleLoadedData() {
		isLoading.value = false;
		hasError.value = false;
		retryAttempted.value = false;
	}

	/**
	 * iPad-Specific error handling
	 * Automates the "toggle" you found that fixes the S3 stall.
	 */
	function handleError() {
		if (!retryAttempted.value && videoElement.value) {
			retryAttempted.value = true;
			const savedUrl = currentVideoUrl.value;
			videoElement.value.src = "";
			videoElement.value.load();
			setTimeout(() => {
				if (videoElement.value && savedUrl) {
					videoElement.value.src = savedUrl;
					videoElement.value.load();
					isLoading.value = true;
				}
			}, 300);
			return;
		}
		isLoading.value = false;
		hasError.value = true;
	}

	function skipBackward() {
		if (!videoElement.value) return;
		videoElement.value.currentTime = Math.max(
			0,
			videoElement.value.currentTime - skipSeconds,
		);
	}

	function skipForward() {
		if (!videoElement.value) return;
		videoElement.value.currentTime = Math.max(
			0,
			videoElement.value.currentTime + skipSeconds,
		);
	}

	function togglePlayPause() {
		if (videoElement.value) {
			if (videoElement.value.paused) {
				videoElement.value.play();
			} else {
				videoElement.value.pause();
			}
		}
	}
	function handleKeyPress(event: KeyboardEvent) {
		if (event.key === "ArrowLeft") {
			skipBackward();
		} else if (event.key === "ArrowRight") {
			skipForward();
		} else if (event.key === " ") {
			event.preventDefault();
			togglePlayPause();
		}
	}

	function selectQuality(quality: string) {
		if (!videoElement.value || selectedQuality.value === quality) {
			isQualityMenuOpen.value = false;
			return;
		}
		const wasPlaying = !videoElement.value.paused;
		const currentTime = videoElement.value.currentTime;
		selectedQuality.value = quality;
		isQualityMenuOpen.value = false;
		retryAttempted.value = false;

		setTimeout(() => {
			if (videoElement.value && currentVideoUrl.value) {
				videoElement.value.src = currentVideoUrl.value;
				videoElement.value.load();
				videoElement.value.addEventListener(
					"loadeddata",
					function restore() {
						if (videoElement.value) {
							videoElement.value.currentTime = currentTime;
							if (wasPlaying)
								videoElement.value.play().catch(() => {});
						}
						videoElement.value?.removeEventListener(
							"loadeddata",
							restore,
						);
					},
					{ once: true },
				);
			}
		}, 0);
	}
	function handleClickOutside(event: MouseEvent) {
		if (
			!(event.target as HTMLElement).closest(".quality-control-container")
		)
			isQualityMenuOpen.value = false;
	}
	function selectPlaylistItem(item: PlaylistItem) {
		if (activePlaylistItem.value?.key === item.key) return;
		const wasPlaying = videoElement.value
			? !videoElement.value.paused
			: false;
		activePlaylistItem.value = item;
		selectedQuality.value = "";
		retryAttempted.value = false;

		setTimeout(() => {
			if (
				videoElement.value &&
				effectiveVideoUrl.value &&
				typeof effectiveVideoUrl.value === "object"
			) {
				const urlObj = effectiveVideoUrl.value as Record<
					string,
					string
				>;
				const keys = Object.keys(urlObj).filter(
					k => k !== "720" && urlObj[k],
				);
				if (keys.length > 0) selectedQuality.value = keys[0];
			}

			if (videoElement.value && currentVideoUrl.value) {
				videoElement.value.src = currentVideoUrl.value;
				videoElement.value.load();
				isLoading.value = true;
				hasError.value = false;

				videoElement.value.addEventListener(
					"loadeddata",
					function restore() {
						if (videoElement.value) {
							videoElement.value.currentTime = 0;
							if (wasPlaying)
								videoElement.value.play().catch(() => {});
						}
						videoElement.value?.removeEventListener(
							"loadeddata",
							restore,
						);
					},
					{ once: true },
				);
			}

			emit("playlistItemChange", item);
		}, 0);
	}

	onMounted(() => {
		if (
			qualityOptions.value.length >= 1 &&
			typeof effectiveVideoUrl.value === "object"
		) {
			selectedQuality.value = getPreferredQuality(qualityOptions.value);
		}
		if (typeof window !== "undefined") {
			isMobileDevice.value =
				window.matchMedia?.("(hover: none)").matches ||
				"ontouchstart" in window ||
				navigator.maxTouchPoints > 0;
		}
		isNativeApp.value = Capacitor.isNativePlatform();
	});

	watch(isQualityMenuOpen, val => {
		val
			? document.addEventListener("click", handleClickOutside)
			: document.removeEventListener("click", handleClickOutside);
	});
</script>

<template>
	<component
		:is="fullPage ? 'div' : DialogLayout"
		@keydown="handleKeyPress"
		:class="{
			'video-window': !fullPage,
			'platform-native-app': isNativeApp && !fullPage,
			'full-page-video-window flex h-full min-h-0 w-full flex-col':
				fullPage,
		}"
	>
		<template #default>
			<div
				class="video-window-content relative flex min-w-0 max-w-full flex-col gap-2 overflow-hidden"
				:class="{
					'px-2 py-2 sm:px-6 sm:py-4': !fullPage,
					'h-full min-h-0 flex-1 p-2 pb-20 landscape:pr-20 landscape:pb-2':
						fullPage,
				}"
			>
				<div
					v-if="!fullPage"
					class="flex w-full items-start justify-between gap-4"
				>
					<div class="flex min-w-0 flex-1 flex-col gap-2">
						<h2 class="video-window-title truncate">
							{{ effectiveVideoTitle }}
						</h2>
					</div>
					<button
						type="button"
						class="video-window-close shrink-0 cursor-pointer p-2"
						@click="emit('close')"
					>
						<Icon
							class="close-btn"
							name="close-btn"
						/>
					</button>
				</div>

				<div
					class="video-and-playlist-row flex min-w-0 flex-col items-center gap-4 lg:flex-row lg:items-stretch lg:justify-center"
					:class="{
						'no-playlist': !isPlaylistMode,
						'min-h-0 flex-1 justify-center overflow-hidden':
							fullPage,
					}"
				>
					<div
						:class="{
							'video-container group relative aspect-video w-full min-w-0 overflow-hidden rounded-lg bg-black lg:max-w-[47.75rem] lg:flex-1 lg:flex-shrink':
								!isIpad,
							'video-container group relative aspect-video w-[85vw] min-w-0 overflow-hidden rounded-lg bg-black lg:flex-[3] lg:flex-shrink':
								isIpad,
							'max-h-full max-w-full': fullPage,
						}"
					>
						<video
							ref="videoElement"
							:key="currentVideoUrl"
							:src="currentVideoUrl"
							class="h-full w-full object-contain"
							:class="{ ipadDevice: isIpad }"
							controls
							playsinline
							webkit-playsinline
							preload="auto"
							crossorigin="anonymous"
							controlslist="nodownload noremoteplayback"
							@loadeddata="handleLoadedData"
							@error="handleError"
						/>

						<div
							v-if="
								!isLoading &&
								!hasError &&
								qualityOptions.length > 1
							"
							class="quality-control-container absolute right-4 top-4 z-20"
							:class="{
								'opacity-100':
									isMobileDevice || showSkipButtons,
								'opacity-70':
									!isMobileDevice && !showSkipButtons,
							}"
						>
							<button
								@click.stop="
									isQualityMenuOpen = !isQualityMenuOpen
								"
								class="quality-dropdown-trigger flex items-center gap-2 rounded-lg bg-black/70 px-4 py-2 text-white backdrop-blur-sm"
							>
								<span>{{
									selectedQuality.toUpperCase() || "720P"
								}}</span>
								<Icon
									:name="
										isQualityMenuOpen
											? 'chevron-up'
											: 'chevron-down'
									"
									class="h-4 w-4"
								/>
							</button>
							<Transition name="fade">
								<div
									v-show="isQualityMenuOpen"
									class="absolute right-0 mt-2 min-w-[7.5rem] overflow-hidden rounded-lg border bg-white py-1 shadow-xl"
								>
									<button
										v-for="qualityOption in qualityOptions"
										:key="qualityOption"
										@click.stop="
											selectQuality(qualityOption)
										"
										class="quality-btn quality-dropdown-option w-full px-4 py-2.5 text-left text-sm font-bold transition-colors"
										:class="
											selectedQuality === qualityOption
												? 'quality-btn-selected'
												: 'quality-btn-unselected'
										"
									>
										{{ qualityOption.toUpperCase() }}
									</button>
								</div>
							</Transition>
						</div>
					</div>

					<div
						v-if="isPlaylistMode && props.playlistItems"
						class="playlist-sidebar flex w-full min-w-0 flex-col lg:h-[26.875rem] lg:w-72 lg:min-w-[18rem] lg:shrink-0"
					>
						<h3 class="mb-3 text-lg font-bold text-gray-800">
							{{ activePlaylistItem?.playlistName || "Playlist" }}
						</h3>
						<div
							class="playlist-scroll-area flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1"
						>
							<button
								v-for="item in props.playlistItems"
								:key="item.key"
								@click="selectPlaylistItem(item)"
								class="playlist-item flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-all"
								:class="
									activePlaylistItem?.key === item.key
										? 'border-green-600 bg-green-50'
										: 'border-gray-200 bg-white'
								"
							>
								<div class="flex min-w-0 flex-1 flex-col gap-1">
									<div class="flex items-center gap-2">
										<span
											class="text-sm font-semibold text-gray-500"
										>
											{{
												t("files.playlistPosition", {
													position: item.playlistPosition,
												})
											}}
										</span>
										<span
											class="truncate font-semibold text-gray-800"
											:class="
												activePlaylistItem?.key ===
												item.key
													? 'text-green-700'
													: ''
											"
										>
											{{ String(item.key).split(".")[0] }}
										</span>
									</div>
									<p
										class="line-clamp-1 text-sm text-gray-600"
									>
										{{ item.description }}
									</p>
								</div>
								<Icon
									v-if="activePlaylistItem?.key === item.key"
									name="play"
									class="h-5 w-5 text-green-600"
								/>
							</button>
						</div>
					</div>
				</div>
			</div>
		</template>
	</component>
</template>

<style scoped>
	:deep(.video-window) {
		max-width: 95vw !important;
		width: 95vw !important;
		margin: 0 auto;
	}

	:deep(.video-window > div) {
		max-width: 100% !important;
		width: 100% !important;
	}
	.video-window-title {
		font-family: "Lato", sans-serif;
		font-size: 1.25rem;
		font-weight: 700;
		color: var(--color-grey-750);
	}
	.playlist-item {
		height: 5rem;
		font-family: "Lato", sans-serif;
	}
	.quality-btn {
		font-family: "Lato", sans-serif;
		font-weight: 700;
		cursor: pointer;
	}
	.close-btn {
		width: 1.25rem;
		height: 1.25rem;
	}
	.quality-dropdown-option {
		font-size: 1.5rem;
		line-height: 0;
	}
	.quality-btn-unselected {
		background-color: var(--color-white);
		color: var(--color-grey-750);
	}
	.quality-btn-unselected:hover {
		background-color: var(--color-surface-hover);
	}
	.quality-btn-selected {
		background-color: var(--color-green-50);
		color: var(--color-green-600);
		border-left: 0.125rem solid var(--color-green-600);
	}

	.playlist-scroll-area {
		-webkit-overflow-scrolling: touch;
		max-height: 40vh;
	}
	@media (min-width: 64rem) {
		.playlist-scroll-area {
			max-height: 100%;
		}

		.lg\:items-stretch {
			align-items: stretch !important;
		}
	}
	.playlist-scroll-area::-webkit-scrollbar {
		width: 0.5rem;
	}
	.playlist-scroll-area::-webkit-scrollbar-thumb {
		background: var(--color-grey-750);
		border-radius: 0.25rem;
	}

	@media (min-width: 48rem) {
		:deep(.platform-native-app) .video-container {
			max-width: none;
		}

		:deep(.platform-native-app) .video-and-playlist-row .video-container {
			flex: 1.4 1 0;
			min-width: 0;
		}
		:deep(.platform-native-app) .video-and-playlist-row .playlist-sidebar {
			width: 18rem;
			min-width: 18rem;
			flex-shrink: 0;
		}

		:deep(.platform-native-app)
			.video-and-playlist-row.no-playlist
			.video-container {
			max-width: 85vw;
			width: 85vw;
		}
	}

	@media (min-width: 64rem) {
		:deep(.platform-native-app) .video-window-content {
			max-width: 100vw !important;
		}
		:deep(.platform-native-app) .video-and-playlist-row {
			width: 100% !important;
			max-width: 100% !important;
		}
		:deep(.platform-native-app) .video-container {
			max-width: none !important;
		}

		:deep(.platform-native-app) .video-and-playlist-row .video-container {
			flex: 3 1 0 !important;
			min-width: 0;
			max-width: none !important;
		}
		:deep(.platform-native-app) .video-and-playlist-row .playlist-sidebar {
			width: 14rem !important;
			min-width: 14rem !important;
			max-width: 14rem !important;
			flex-shrink: 0;
		}

		:deep(.platform-native-app)
			.video-and-playlist-row.no-playlist
			.video-container {
			max-width: 95vw !important;
			width: 95vw !important;
			flex: none !important;
		}
	}

	@media (max-height: 31.25rem) and (orientation: landscape) {
		.video-and-playlist-row {
			flex-direction: row !important;
			align-items: center !important;
			height: 70vh;
		}

		.video-container {
			flex: 3 1 0 !important;
			height: 100% !important;
		}

		.playlist-sidebar {
			flex: 1 1 0 !important;
			max-width: 15.25 !important;
			height: 100% !important;
		}
	}
	.fade-enter-active,
	.fade-leave-active {
		transition:
			opacity 0.15s,
			transform 0.15s;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
		transform: scale(0.95);
	}
	.ipadDevice {
		width: 100%;
		height: 100%;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
		background: var(--color-black);
		border-radius: 0;
	}
</style>
